export type ChatMarkdownInline =
  | { type: "text"; value: string }
  | { type: "strong"; children: ChatMarkdownInline[] }
  | { type: "em"; children: ChatMarkdownInline[] }
  | { type: "code"; value: string };

export type ChatMarkdownBlock =
  | { type: "heading"; level: 2 | 3; children: ChatMarkdownInline[] }
  | { type: "paragraph"; children: ChatMarkdownInline[] }
  | { type: "list"; items: ChatMarkdownInline[][] }
  | { type: "code"; value: string }
  | { type: "break" };

export type ChatMarkdownAst = ChatMarkdownBlock[];

function textNode(value: string): ChatMarkdownInline[] {
  return value ? [{ type: "text", value }] : [];
}

function splitMarked(
  text: string,
  pattern: RegExp,
  wrap: (inner: string) => ChatMarkdownInline,
  parseRest: (value: string) => ChatMarkdownInline[],
): ChatMarkdownInline[] {
  const nodes: ChatMarkdownInline[] = [];
  let lastIndex = 0;
  const regex = new RegExp(
    pattern.source,
    pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`,
  );
  for (const match of text.matchAll(regex)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      nodes.push(...parseRest(text.slice(lastIndex, index)));
    }
    nodes.push(wrap(match[1] ?? ""));
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(...parseRest(text.slice(lastIndex)));
  }
  return nodes.length > 0 ? nodes : textNode(text);
}

function parseCode(text: string): ChatMarkdownInline[] {
  return splitMarked(
    text,
    /`([^`]+)`/g,
    (value) => ({ type: "code", value }),
    textNode,
  );
}

function parseEmphasis(text: string): ChatMarkdownInline[] {
  return splitMarked(
    text,
    /\*([^*]+)\*/g,
    (value) => ({ type: "em", children: parseCode(value) }),
    parseCode,
  );
}

export function parseInlineMarkdown(text: string): ChatMarkdownInline[] {
  return splitMarked(
    text,
    /\*\*(.+?)\*\*/g,
    (value) => ({ type: "strong", children: parseEmphasis(value) }),
    parseEmphasis,
  );
}

export function parseChatMarkdown(text: string): ChatMarkdownAst {
  const lines = text.split("\n");
  const blocks: ChatMarkdownAst = [];
  let listItems: ChatMarkdownInline[][] | null = null;

  const flushList = () => {
    if (!listItems) return;
    blocks.push({ type: "list", items: listItems });
    listItems = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line === undefined) continue;
    const nextLine = lines[i + 1];

    const h2Match = line.match(/^##\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);
    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    const codeMatch = line.match(/^`([^`]+)`$/);

    if (h2Match) {
      flushList();
      blocks.push({
        type: "heading",
        level: 2,
        children: [{ type: "text", value: h2Match[1] ?? "" }],
      });
      continue;
    }
    if (h3Match) {
      flushList();
      blocks.push({
        type: "heading",
        level: 3,
        children: [{ type: "text", value: h3Match[1] ?? "" }],
      });
      continue;
    }
    if (ulMatch) {
      if (!listItems) listItems = [];
      listItems.push(parseInlineMarkdown(ulMatch[1] ?? ""));
      continue;
    }
    if (codeMatch) {
      flushList();
      blocks.push({ type: "code", value: codeMatch[1] ?? "" });
      continue;
    }

    flushList();

    if (line.trim() === "" && nextLine?.trim() === "") {
      blocks.push({ type: "break" });
      continue;
    }

    if (line.trim() !== "") {
      blocks.push({ type: "paragraph", children: parseInlineMarkdown(line) });
    } else {
      blocks.push({ type: "break" });
    }
  }
  flushList();
  return blocks;
}

export function escapeChatMarkdownHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlinesToHtml(nodes: ChatMarkdownInline[]): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case "text":
          return escapeChatMarkdownHtml(node.value);
        case "strong":
          return `<strong>${inlinesToHtml(node.children)}</strong>`;
        case "em":
          return `<em>${inlinesToHtml(node.children)}</em>`;
        case "code":
          return `<code>${escapeChatMarkdownHtml(node.value)}</code>`;
      }
    })
    .join("");
}

export function chatMarkdownToHtml(ast: ChatMarkdownAst): string {
  return ast
    .map((block) => {
      switch (block.type) {
        case "heading":
          return `<h${block.level}>${inlinesToHtml(block.children)}</h${block.level}>`;
        case "paragraph":
          return `<p>${inlinesToHtml(block.children)}</p>`;
        case "list":
          return `<ul>${block.items
            .map((item) => `<li>${inlinesToHtml(item)}</li>`)
            .join("")}</ul>`;
        case "code":
          return `<code>${escapeChatMarkdownHtml(block.value)}</code>`;
        case "break":
          return "<br />";
      }
    })
    .join("");
}
